'use client';

import { Disclosure } from '@headlessui/react';
import Image from 'next/image';
import { Document, BLOCKS } from '@contentful/rich-text-types';

import { RichText, Links } from '../../lib/copyRendering';
import { Column, Row, Section } from '../Section';
import { Text } from '../Text';
import { CommonQuestionsFragment } from './CommonQuestions.generated';
import chevronInCircleIcon from './chevron-in-circle.svg';

interface CommonQuestionsProps {
  snippetGroup: CommonQuestionsFragment | null | undefined;
  inset?: boolean;
  blueBackground?: boolean;
}

export const CommonQuestions: React.FC<CommonQuestionsProps> = ({
  snippetGroup,
  inset,
  blueBackground,
}) => {
  if (!snippetGroup) {
    return null;
  }

  // Supports nested accordions by grouping content inside a Snippet
  const renderContent = (
    content: { json: Document; links?: Links } | null | undefined,
    snippetKey: string,
  ) => {
    if (!content) return null;

    // Create new group when an H1 is encountered so we can make it collapsible.
    // All content after an H1 until the next H1 is encountered will be grouped into an accordion under the H1.
    const groups = content.json.content.reduce<
      { json: Document; links?: Links }[]
    >((groups, block) => {
      const previousGroups = groups.slice(0, -1);
      const currentGroup = groups.slice(-1)[0];

      const shouldCreateNewGroup = block.nodeType === BLOCKS.HEADING_1;

      return [
        ...previousGroups,
        {
          json: {
            ...content.json,
            content: [
              ...(currentGroup?.json.content ?? []),
              ...(shouldCreateNewGroup ? [] : [block]),
            ],
          },
          links: content.links,
        },
        ...(shouldCreateNewGroup
          ? [
              {
                json: {
                  ...content.json,
                  content: [block],
                },
                links: content.links,
              },
            ]
          : []),
      ];
    }, []);

    return groups.map(({ json, links }, idx) => {
      const blocks = json.content;

      // If first block of group is H1, make it collapsible, otherwise render it normally
      return blocks[0].nodeType === BLOCKS.HEADING_1 ? (
        <Disclosure key={`${snippetKey}-disclosure-${idx}`}>
          {({ open }) => (
            <div className="py-5">
              <Disclosure.Button className="flex w-full justify-between items-center rounded-lg px-4 py-2 text-left text-sm font-medium hover:text-blue-500 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
                <Text.H3 className="!text-xl md:!text-2xl text-inherit">
                  <RichText
                    // Render first block (the H1) as paragraph block since we're wrapping it in a heading
                    content={{
                      json: {
                        nodeType: BLOCKS.DOCUMENT,
                        content: [
                          {
                            ...blocks[0],
                            nodeType: BLOCKS.PARAGRAPH,
                          },
                        ],
                        data: {},
                      },
                      links,
                    }}
                  />
                </Text.H3>
                <Image
                  src={chevronInCircleIcon}
                  alt={open ? 'Open arrow' : 'Close arrow'}
                  className={`${
                    open ? 'rotate-180 transform' : ''
                  } transition-transform flex-shrink-0 ml-4`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">
                <RichText
                  // Render the rest of the blocks inside as accordion content
                  content={{
                    json: { ...json, content: blocks.slice(-1) },
                    links,
                  }}
                />
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
      ) : (
        <RichText
          content={{
            json,
            links,
          }}
        />
      );
    });
  };

  return (
    <Section background={blueBackground ? 'BLUE' : 'WHITE'}>
      <Row inset={inset}>
        <Column>
          <Text.H2 className="text-center">{snippetGroup.name}</Text.H2>
          <div className="divide-y">
            {snippetGroup.snippetsCollection?.items.map((snippet) => {
              if (snippet?.__typename !== 'Snippet') {
                return null;
              }
              return (
                <Disclosure key={snippet.key}>
                  {({ open }) => (
                    <div className="py-5">
                      <Disclosure.Button className="flex w-full justify-between items-center rounded-lg px-4 py-2 text-left text-sm font-medium hover:text-blue-500 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
                        <Text.H3 className="!text-xl md:!text-2xl text-inherit">
                          {snippet.name}
                        </Text.H3>
                        <Image
                          src={chevronInCircleIcon}
                          alt={open ? 'Open arrow' : 'Close arrow'}
                          className={`${
                            open ? 'rotate-180 transform' : ''
                          } transition-transform flex-shrink-0 ml-4`}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">
                        {renderContent(snippet.content, snippet.key ?? '')}
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>
              );
            })}
          </div>
        </Column>
      </Row>
    </Section>
  );
};
